/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VehicleComponentsEditDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography } from "@mui/material";
import EnterSelectTextField from "../text-fields/EnterSelectTextField";
import {
  HandleApiActions,
  adminSettingsAPI,
  labCar,
} from "../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import CustomDialogBoxV2 from "../customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomButtonV2 from "../customComponentsV2/common/button/CustomButtonV2";
import CustomFormTextFieldV2 from "../customComponentsV2/common/textField/CustomFormTextFieldV2";
import MultiselectConnectorV2 from "../customComponentsV2/common/connectors/MultiselectConnectorV2";
import CustomCheckBoxV2 from "../customComponentsV2/common/checkBox/CustomCheckBoxV2";
import AttributesForm from "../../pages/labcar/Attributes/AttributesForm";
import CustomTabsV2 from "../customComponentsV2/common/tabs/CustomTabsV2";
import CustomSelectV2 from "../customComponentsV2/common/select/CustomSelectV2";

const getUniqueVariantIds = (...variantIdLists) => {
  const variantIds = variantIdLists
    .flatMap((variantIdsList) =>
      Array.isArray(variantIdsList) ? variantIdsList : [variantIdsList],
    )
    .filter((variantId) => variantId !== undefined && variantId !== null);

  return [
    ...new Map(variantIds.map((variantId) => [String(variantId), variantId]))
      .values(),
  ];
};

const getItemVariantIds = (item) =>
  getUniqueVariantIds(
    item?.variant_ids,
    item?.vehicle_component_details?.variant_ids,
    item?.vc_details?.variant_ids,
  );

const getVehicleComponentVariantIds = (
  item,
  selectedSingleVariantDetails,
) =>
  getUniqueVariantIds(
    selectedSingleVariantDetails?.variant_id,
    getItemVariantIds(item),
  );

const getVCFieldValue = (item, keys = [], fallback = "") => {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  const vcDetails = item?.vehicle_component_details || item?.vc_details;
  for (const key of keys) {
    const value = vcDetails?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return fallback;
};

function VehicleComponentsEditDialog({
  openDialog,
  setOpenDialog,
  title = "Edit Vehicle Component",
  viewArchived = false,
  selectedVCItem,
  variantOptionList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  selectedZoneDetails,
  supplierOptionsList,
  vcOptionsList = [],
  selectedVehicleSystemId,
  selectedVehicleSystemDetails,
  getVehicleComponents,
}) {
  const dispatch = useDispatch();
  const adminSettingsDetails = useSelector(
    (state) => state.adminSettingsReducer,
  );

  const [selectedVehicleComponent, setSelectedVehicleComponent] = useState("");
  const [componentNo, setComponentNo] = useState("");
  const [description, setDescription] = useState("");
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [keywordOptions, setKeywordOptions] = useState([]);
  const [selectedKeywordIds, setSelectedKeywordIds] = useState([]);
  const [reason, setReason] = useState("");
  const [addInSettings, setAddInSettings] = useState(false);
  const [addInSettingsReason, setAddInSettingsReason] = useState("");
  const [validateShowAddInSettings, setValidateShowAddInSettings] =
    useState(false);
  const [vsVariantOptionList, setVsVariantOptionList] = useState([]);
  const [selectedVCDetailsForVariants, setSelectedVCDetailsForVariants] =
    useState(null);

  const [selectedZoneName, setSelectedZoneName] = useState("");
  const [selectedVSId, setSelectedVSId] = useState("");
  const [parentVSOptions, setParentVSOptions] = useState([]);
  const [zoneVSHistory, setZoneVSHistory] = useState({});

  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [hasPendingVCAddRequest, setHasPendingVCAddRequest] = useState(false);
  const [attributeFormData, setAttributeFormData] = useState({
    added: [],
    edited: [],
    deleted: [],
  });
  const [vcEditTabValue, setVcEditTabValue] = useState(0);

  const normalizeSupplierOptions = (list) => {
    if (!Array.isArray(list)) return [];
    return list
      .map((opt) => {
        if (!opt) return null;
        if (typeof opt === "string") return { value: opt, label: opt };
        const value = opt.value ?? opt.id ?? opt.name ?? opt.label;
        const label = opt.label ?? opt.name ?? opt.value ?? opt.id;
        if (value == null || label == null) return null;
        return { value, label };
      })
      .filter(Boolean);
  };

  const suppliersFromProps = useMemo(
    () => normalizeSupplierOptions(supplierOptionsList),
    [supplierOptionsList],
  );
  const suppliersFromStore = useMemo(() => {
    const list = adminSettingsDetails?.adminSettings?.data?.ecu_suppliers;
    return normalizeSupplierOptions(list);
  }, [adminSettingsDetails]);
  const resolvedSupplierOptionsList =
    suppliersFromProps.length > 0 ? suppliersFromProps : suppliersFromStore;

  const normalizeKeywordIds = (keywords) =>
    (Array.isArray(keywords) ? keywords : [])
      .map((keyword) =>
        keyword && typeof keyword === "object"
          ? keyword?.id ?? keyword?.value
          : keyword,
      )
      .filter((id) => id !== null && id !== undefined && id !== "");

  useEffect(() => {
    if (!openDialog) return;
    if (resolvedSupplierOptionsList.length > 0) return;

    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_admin_settings,
        show_toast: false,
      }),
    );
    setVcEditTabValue(0);
  }, [openDialog, resolvedSupplierOptionsList.length, dispatch]);

  useEffect(() => {
    if (!openDialog) return;

    dispatch(
      HandleApiActions({
        api_name: "/SCM/settings/view/keywords",
        https_method: "GET",
        params: { type: 3 },
        ignore_app_loading_spinner: true,
      }),
    ).then((response) => {
      const keywords = Array.isArray(response?.data) ? response.data : [];
      setKeywordOptions(
        keywords.map((keyword) => ({
          value: keyword?.id,
          label: keyword?.name,
        })),
      );
    });
  }, [dispatch, openDialog]);

  useEffect(() => {
    setAddInSettings(false);
    setAddInSettingsReason("");
  }, [selectedVehicleComponent]);

  useEffect(() => {
    if (!openDialog) return;
    const vcName = String(selectedVehicleComponent || "").trim();
    if (!vcName) {
      setHasPendingVCAddRequest(false);
      return;
    }

    let isActive = true;
    const checkVCAddRequest = async () => {
      const resp = await Promise.resolve(dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_vc_add_requests,
          show_toast: false,
          params: {
            status: 100,
            vc_name: vcName,
          },
        }),
      ));
      if (!isActive) return;
      const hasPending = !resp?.error && Number(resp?.total_count || 0) > 0;
      setHasPendingVCAddRequest(hasPending);
      if (hasPending) {
        setAddInSettings(false);
        setAddInSettingsReason("");
      }
    };
    void checkVCAddRequest();

    return () => {
      isActive = false;
    };
  }, [openDialog, selectedVehicleComponent, dispatch]);

  useEffect(() => {
    const filteredVcOptionsList =
      vcOptionsList?.map((vcOL) => vcOL?.label?.toUpperCase()) ?? [];
    const showAddInSettings = selectedVehicleComponent
      ? filteredVcOptionsList.includes(selectedVehicleComponent?.toUpperCase())
      : false;
    setValidateShowAddInSettings(showAddInSettings || false);
  }, [vcOptionsList, selectedVehicleComponent]);

  useEffect(() => {
    const variantIds = new Set(getUniqueVariantIds(
      selectedSingleVariantDetails?.variant_id,
      selectedVehicleSystemDetails?.variant_ids,
      getItemVariantIds(selectedVCItem),
      getItemVariantIds(selectedVCDetailsForVariants),
    ).map(String));
    const filteredVariantOptionList = (variantOptionList || []).filter(
      (option) => variantIds.has(String(option?.value)),
    );

    setVsVariantOptionList(filteredVariantOptionList);
  }, [
    variantOptionList,
    selectedVehicleSystemDetails,
    selectedSingleVariantDetails,
    selectedVCItem,
    selectedVCDetailsForVariants,
  ]);

  const setFormFromItem = useCallback((item) => {
    const initialComponentNo = getVCFieldValue(
      item,
      ["component_number", "vc_number"],
      "",
    );
    const initialName = getVCFieldValue(
      item,
      ["name", "component_name", "vehicle_component_name", "vc_name"],
      "",
    );
    const initialDescription = getVCFieldValue(item, ["description"], "");
    const initialSuppliers =
      item?.suppliers || item?.vehicle_component_details?.suppliers || [];
    const initialKeywordIds = normalizeKeywordIds(
      item?.keywords || item?.vehicle_component_details?.keywords,
    );
    const initialVariantIds = new Set(getVehicleComponentVariantIds(
      item,
      selectedSingleVariantDetails,
    ).map(String));
    const initialVariants = (variantOptionList || [])
      .filter((variant) => initialVariantIds.has(String(variant?.value)))
      .map((variant) => variant?.value);

    setComponentNo(initialComponentNo);
    setSelectedVehicleComponent(initialName);
    setDescription(initialDescription);
    setSelectedSuppliers(initialSuppliers);
    setSelectedKeywordIds(initialKeywordIds);
    setSelectedVariants(initialVariants);
    setReason("");
    setAddInSettings(false);
    setAddInSettingsReason("");
  }, [selectedSingleVariantDetails, variantOptionList]);

  useEffect(() => {
    if (!openDialog || !selectedVCItem) return;
    setAttributeFormData({ added: [], edited: [], deleted: [] });
    setSelectedVCDetailsForVariants(null);
    setVcEditTabValue(0);

    const initialZone =
      selectedVCItem?.zone_details?.zone_name ||
      selectedZoneDetails?.zone_name ||
      selectedZoneDetails?.zone_position_name ||
      selectedProjectDetails?.project_zones?.find(
        (z) => z?.position === selectedVCItem?.zone_position,
      )?.zone_name ||
      "";
    const initialVS = selectedVehicleSystemId || selectedVCItem?.vs_id || "";
    const initialVSStr = initialVS ? String(initialVS) : "";

    setSelectedZoneName(initialZone);
    setSelectedVSId(initialVSStr);
    if (initialZone && initialVSStr) {
      setZoneVSHistory({ [initialZone]: initialVSStr });
    } else if (initialVSStr) {
      setZoneVSHistory({ default: initialVSStr });
    } else {
      setZoneVSHistory({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog, selectedVCItem]);

  useEffect(() => {
    if (!openDialog) return;

    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    if (!projectId || !variantId) {
      setParentVSOptions([]);
      return;
    }

    const isZonal = !!selectedProjectDetails?.is_zonal;
    let zonePosition;
    if (isZonal) {
      if (!selectedZoneName) {
        setParentVSOptions([]);
        return;
      }
      const matchedZone = selectedProjectDetails?.project_zones?.find(
        (z) =>
          z?.zone_name === selectedZoneName ||
          z?.position === selectedZoneName ||
          String(z?.position) === String(selectedZoneName),
      );
      zonePosition = matchedZone?.position;
    }

    const params = {
      project_id: Number(projectId),
      variant_id: Number(variantId),
      page_number: 1,
      page_size: 1000,
      archive: (title === 'Draft Vehicle Component' || title?.toLowerCase()?.includes('draft')) ? false : !!viewArchived,
      ...(isZonal && zonePosition !== undefined && { zone_position: zonePosition }),
    };

    dispatch(
      HandleApiActions({
        ...labCar.get_vehicle_systems_all,
        params,
        show_toast: false,
      }),
    ).then((response) => {
      const data = response?.data?.data || response?.data || [];
      const list = Array.isArray(data) ? data : [];
      const options = list.map((vs) => ({
        value: String(vs?.id || vs?.vs_id),
        label: vs?.assembly_number ? String(vs.assembly_number) : String(vs?.id || vs?.vs_id),
      }));
      setParentVSOptions(options);

      const zoneKey = isZonal ? selectedZoneName : "default";
      const rememberedVS = zoneVSHistory[zoneKey];
      const hasRemembered = options.some((opt) => opt.value === rememberedVS);

      if (hasRemembered) {
        setSelectedVSId(rememberedVS);
      } else if (options.length > 0) {
        setSelectedVSId(options[0].value);
        if (zoneKey) {
          setZoneVSHistory((prev) => ({
            ...prev,
            [zoneKey]: options[0].value,
          }));
        }
      } else {
        setSelectedVSId("");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    openDialog,
    selectedProjectDetails,
    selectedSingleVariantDetails,
    selectedZoneName,
    viewArchived,
    dispatch,
  ]);

  useEffect(() => {
    if (!openDialog || !selectedVCItem) return;
    const fetchAndSetVCDetails = async () => {
      const projectId = selectedProjectDetails?.project_id;
      const variantId = selectedSingleVariantDetails?.variant_id;
      const vsId = selectedVehicleSystemId;
      const vcId = selectedVCItem?.id || selectedVCItem?.vc_id;

      setFormFromItem(selectedVCItem);

      if (!projectId || !variantId || !vsId || !vcId) return;

      setIsLoadingDetails(true);
      const resp = await Promise.resolve(dispatch(
        HandleApiActions({
          ...labCar.get_vehicle_components_all,
          show_toast: false,
          params: {
            project_id: Number(projectId),
            variant_id: Number(variantId),
            vs_id: Number(vsId),
            vc_id: Number(vcId),
          },
        }),
      ));
      setIsLoadingDetails(false);

      if (resp?.error) return;
      const matchedDetails = (resp?.data || []).find(
        (vc) =>
          Number(vc?.id) === Number(vcId) || Number(vc?.vc_id) === Number(vcId),
      );
      if (matchedDetails) {
        setSelectedVCDetailsForVariants(matchedDetails);
        setFormFromItem(matchedDetails);
      }
    };

    void fetchAndSetVCDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    openDialog,
    selectedVCItem,
    selectedSingleVariantDetails?.variant_id,
    selectedProjectDetails?.project_id,
    selectedVehicleSystemId,
    dispatch,
  ]);



  const isDialogDetailsValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
    const isBasicValid =
      componentNo?.trim() !== "" &&
      regexMin3.test(componentNo.trim()) &&
      selectedVehicleComponent?.trim()?.length > 0 &&
      description?.trim()?.length >= 3 &&
      selectedSuppliers?.length > 0 &&
      (addInSettings ? addInSettingsReason?.trim()?.length >= 3 : true);

    if (title === "Draft Vehicle Component" || viewArchived) {
      const isZoneValid =
        !selectedProjectDetails?.is_zonal || Boolean(selectedZoneName);
      const isVSValid = Boolean(selectedVSId);
      return isBasicValid && isZoneValid && isVSValid;
    }

    return isBasicValid;
  };

  const isDialogFieldsValid = () =>
    isDialogDetailsValid() && reason?.trim()?.length >= 3;

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setAttributeFormData({ added: [], edited: [], deleted: [] });
    setReason("");
    setVcEditTabValue(0);
  };

  const handleVCAttributeAdd = useCallback((data = {}) => {
    const added = Array.isArray(data?.added) ? data.added : [];
    const edited = Array.isArray(data?.edited) ? data.edited : [];
    const deleted = Array.isArray(data?.deleted) ? data.deleted : [];

    setAttributeFormData((prev) => {
      const nextData = { added, edited, deleted };
      return JSON.stringify(prev) === JSON.stringify(nextData)
        ? prev
        : nextData;
    });
  }, []);

  const attributeFormRows = useMemo(() => {
    const addedAttributes = Array.isArray(attributeFormData?.added)
      ? attributeFormData.added
      : [];
    const editedAttributes = Array.isArray(attributeFormData?.edited)
      ? attributeFormData.edited
      : [];
    const deletedAttributeIds = Array.isArray(attributeFormData?.deleted)
      ? attributeFormData.deleted.map(String)
      : [];

    const editedAttributeMap = new Map(
      editedAttributes.map((attribute) => [String(attribute?.id), attribute]),
    );

    const existingAttributes = (selectedVCItem?.attributes || [])
      .filter((attribute) => {
        const attributeId = attribute?.attribute_id ?? attribute?.id;
        return !deletedAttributeIds.includes(String(attributeId));
      })
      .map((attribute) => {
        const attributeId = attribute?.attribute_id ?? attribute?.id;
        const editedAttribute = editedAttributeMap.get(String(attributeId));

        if (!editedAttribute) {
          return attribute;
        }

        return {
          ...attribute,
          attribute_id: attributeId,
          attribute_type: editedAttribute.attribute_type,
          attribute_sub_type: editedAttribute.attribute_sub_type,
          attribute_heading: editedAttribute.attribute_heading,
          attribute_value: editedAttribute.attribute_value,
          edited: true,
        };
      });

    const addedAttributeRows = addedAttributes.map((attribute) => ({
      ...attribute,
      attribute_id: null,
      newlyAdded: true,
      edited: false,
    }));

    return [...existingAttributes, ...addedAttributeRows];
  }, [
    attributeFormData?.added,
    attributeFormData?.deleted,
    attributeFormData?.edited,
    selectedVCItem?.attributes,
  ]);

  const handleEditVehicleComponent = async () => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const vcId = selectedVCItem?.id;
    const addedAttributes = Array.isArray(attributeFormData?.added)
      ? attributeFormData.added
      : [];
    const updatedAttributes = Array.isArray(attributeFormData?.edited)
      ? attributeFormData.edited
      : [];
    const deletedAttributes = Array.isArray(attributeFormData?.deleted)
      ? attributeFormData.deleted
      : [];

    const currentVSId = selectedVehicleSystemId || selectedVCItem?.vs_id;
    if (!projectId || !variantId || !vcId || !currentVSId) return;

    if (
      addedAttributes.length > 0 ||
      updatedAttributes.length > 0 ||
      deletedAttributes.length > 0
    ) {
      await Promise.resolve(dispatch(
        HandleApiActions({
          ...labCar.updateVCAttribute,
          params: {
            entity_id: Number(selectedVCItem?.id),
            new_attributes: addedAttributes,
            updated_attributes: updatedAttributes,
            deleted_attributes: deletedAttributes,
          },
          show_toast: false,
        }),
      ));
    }

    const isDraftVC = title === "Draft Vehicle Component" || Boolean(viewArchived);
    const newVSId = selectedVSId ? Number(selectedVSId) : undefined;
    const isVSChanged =
      newVSId !== undefined &&
      newVSId !== Number(currentVSId);

    const resp = await Promise.resolve(dispatch(
      HandleApiActions({
        ...labCar.edit_vehicle_component,
        show_toast: true,
        params: {
          project_id: Number(projectId),
          variant_id: Number(variantId),
          vs_id: Number(currentVSId),
          ...(isDraftVC && isVSChanged && { new_vs_id: newVSId }),
          vc_id: Number(vcId),
          component_number: componentNo?.trim(),
          name: selectedVehicleComponent,
          description,
          suppliers: selectedSuppliers,
          keywords: selectedKeywordIds?.map(Number) || [],
          current_time: new Date().toISOString(),
          reason: reason?.trim(),
          ...(addInSettings
            ? {
              add_vc_admin_vs: true,
              add_vc_reason: addInSettingsReason?.trim(),
            }
            : {}),
        },
      }),
    ));
    if (!resp?.error) {
      handleCloseDialog();
      getVehicleComponents();
    }
  };

  const submitButtonDisabled =
    (vcEditTabValue === 0
      ? !isDialogDetailsValid()
      : !isDialogFieldsValid()) || isLoadingDetails;

  const isAddAction = title === "Draft Vehicle Component" || viewArchived;
  let submitButtonName = "Save";
  if (vcEditTabValue === 0) {
    submitButtonName = "Next";
  } else if (isAddAction) {
    submitButtonName = "Add";
  }

  return (
    <CustomDialogBoxV2
      open={openDialog}
      onClose={handleCloseDialog}
      title={title}
      dialogActions={
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box>
            {vcEditTabValue === 1 && (
              <CustomButtonV2
                type="cancel"
                size="small"
                btnName="Back"
                handleOnClick={() => {
                  setVcEditTabValue(0);
                }}
              />
            )}
          </Box>

          <CustomButtonV2
            type="submit"
            size="small"
            disabled={submitButtonDisabled}
            btnName={submitButtonName}
            handleOnClick={() => {
              if (vcEditTabValue === 0) {
                setVcEditTabValue(1);
              } else {
                void handleEditVehicleComponent();
              }
            }}
          />
        </Box>
      }
    >
      <CustomTabsV2
        defaultTab={vcEditTabValue}
        value={vcEditTabValue}
        onTabChange={(newValue) => setVcEditTabValue(newValue)}
        tabs={[
          {
            name: "Details",
            content: (
              <Stack spacing={2} mb={2} sx={{ width: "450px" }}>
                {(title === "Draft Vehicle Component" || viewArchived) &&
                  title !== "Edit Vehicle Component" &&
                  selectedProjectDetails?.is_zonal && (
                    <CustomSelectV2
                      size={"small"}
                      label={"Select Zone"}
                      required={true}
                      optionsList={
                        selectedProjectDetails?.project_zones?.map((zone) => ({
                          value: zone?.zone_name,
                          label: zone?.zone_name,
                        })) || []
                      }
                      selectedValue={selectedZoneName || " "}
                      handleOnChange={(e) => {
                        setSelectedZoneName(e.target.value);
                        setSelectedVSId("");
                      }}
                    />
                  )}
                {(title === "Draft Vehicle Component" || viewArchived) &&
                  title !== "Edit Vehicle Component" && (
                    <CustomSelectV2
                      size={"small"}
                      label={"Select Parent"}
                    required={true}
                    optionsList={parentVSOptions}
                    selectedValue={selectedVSId || " "}
                    handleOnChange={(e) => {
                      const val = e.target.value;
                      setSelectedVSId(val);
                      const zoneKey = selectedProjectDetails?.is_zonal
                        ? selectedZoneName
                        : "default";
                      if (zoneKey) {
                        setZoneVSHistory((prev) => ({
                          ...prev,
                          [zoneKey]: val,
                        }));
                      }
                    }}
                  />
                )}
                <CustomFormTextFieldV2
                  size={"small"}
                  label={"Component No"}
                  required={true}
                  type={"text"}
                  fldType={"textR"}
                  value={componentNo}
                  setValue={(value) =>
                    value.length <= 40 && setComponentNo(value)
                  }
                  maxLength={40}
                />
                <EnterSelectTextField
                  size={"small"}
                  label={"Select / Enter Vehicle Component"}
                  required={true}
                  optionsList={vcOptionsList}
                  value={String(selectedVehicleComponent || "")}
                  setValue={setSelectedVehicleComponent}
                  height={{ height: "32px" }}
                />
                <CustomFormTextFieldV2
                  size={"small"}
                  label={"Description"}
                  required={true}
                  value={description}
                  setValue={(value) =>
                    value.length <= 500 && setDescription(value)
                  }
                  maxLength={500}
                />
                <MultiselectConnectorV2
                  required
                  mutiple
                  size={"small"}
                  label={"Select Suppliers"}
                  optionsList={resolvedSupplierOptionsList?.map((item) => ({
                    value: item?.value.toUpperCase(),
                    label: item?.label,
                  }))}
                  selectedValues={selectedSuppliers || []}
                  setSelectedValues={setSelectedSuppliers}
                  placeholder={false}
                />
                <MultiselectConnectorV2
                  mutiple
                  selectedValues={selectedKeywordIds || []}
                  setSelectedValues={setSelectedKeywordIds}
                  label={"Keywords"}
                  optionsList={keywordOptions}
                  placeholder={false}
                />
                <MultiselectConnectorV2
                  mutiple
                  selectedValues={selectedVariants || []}
                  setSelectedValues={setSelectedVariants}
                  label={"Select Variants"}
                  optionsList={vsVariantOptionList}
                  placeholder={false}
                  immutableValues={
                    selectedSingleVariantDetails?.variant_id == null
                      ? []
                      : [selectedSingleVariantDetails?.variant_id]
                  }
                  disabled={true}
                />

                {selectedVehicleComponent && !validateShowAddInSettings && (
                  <Box display="flex" alignItems="center">
                    <CustomCheckBoxV2
                      checked={addInSettings}
                      disabled={hasPendingVCAddRequest}
                      onChange={() => setAddInSettings((prev) => !prev)}
                      sx={{ pl: 0 }}
                    />
                    <Typography sx={{ cursor: "default" }}>
                      Request to add in settings
                    </Typography>
                  </Box>
                )}
                {addInSettings &&
                  selectedVehicleComponent &&
                  !validateShowAddInSettings && (
                  <CustomFormTextFieldV2
                    size={"small"}
                    label={"Reason for settings request"}
                    required={true}
                    value={addInSettingsReason}
                    setValue={(value) =>
                      value.length <= 100 && setAddInSettingsReason(value)
                    }
                    maxLength={100}
                  />
                )}
              </Stack>
            ),
          },
          {
            name: "Attribute",
            content: (
              <Stack
                spacing={2}
                mb={2}
                justifyContent={"center"}
                sx={{ width: "450px" }}
              >
                <AttributesForm
                  type={"EDIT"}
                  attributeFromApi={attributeFormRows}
                  getFormData={handleVCAttributeAdd}
                  reason={reason}
                  setReason={setReason}
                  showReason={true}
                />
              </Stack>
            ),
          },
        ]}
      />
    </CustomDialogBoxV2>
  );
}

VehicleComponentsEditDialog.propTypes = {
  openDialog: PropTypes.bool,
  setOpenDialog: PropTypes.func,
  title: PropTypes.string,
  viewArchived: PropTypes.bool,
  selectedVCItem: PropTypes.shape({
    id: PropTypes.any,
    vc_id: PropTypes.any,
    vs_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    zone_position: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    zone_details: PropTypes.shape({
      zone_name: PropTypes.string,
    }),
    attributes: PropTypes.array,
  }),
  variantOptionList: PropTypes.array,
  selectedProjectDetails: PropTypes.shape({
    project_id: PropTypes.any,
    is_zonal: PropTypes.bool,
    project_zones: PropTypes.array,
  }),
  selectedSingleVariantDetails: PropTypes.shape({
    variant_id: PropTypes.any,
  }),
  selectedZoneDetails: PropTypes.shape({
    position: PropTypes.any,
    zone_name: PropTypes.string,
    zone_position_name: PropTypes.string,
  }),
  supplierOptionsList: PropTypes.array,
  vcOptionsList: PropTypes.array,
  selectedVehicleSystemId: PropTypes.any,
  selectedVehicleSystemDetails: PropTypes.shape({
    variant_ids: PropTypes.array,
  }),
  getVehicleComponents: PropTypes.func,
};

export default VehicleComponentsEditDialog;
